import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { logEvent } from 'firebase/analytics';
import { doc, getDoc } from 'firebase/firestore';
import { auth, analytics, db } from '../api/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Google Auth Provider with custom parameters
  const googleProvider = new GoogleAuthProvider();
  
  // Add additional scopes if needed
  googleProvider.addScope('email');
  googleProvider.addScope('profile');
  
  // Set custom parameters to improve user experience
  googleProvider.setCustomParameters({
    prompt: 'select_account',
    display: 'popup'
  });

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      console.log('Starting Google sign-in...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Google sign-in successful:', result.user);
      
      // Track successful sign-in
      logEvent(analytics, 'login', {
        method: 'google',
        user_id: result.user.uid
      });
      
      return result.user;
    } catch (error) {
      console.error('Detailed Google sign-in error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Provide more specific error messages
      let userFriendlyMessage = 'Failed to sign in. Please try again.';
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          userFriendlyMessage = 'Sign-in was cancelled. Please try again.';
          break;
        case 'auth/popup-blocked':
          userFriendlyMessage = 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
          break;
        case 'auth/unauthorized-domain':
          userFriendlyMessage = 'This domain is not authorized for OAuth operations.';
          break;
        case 'auth/operation-not-allowed':
          userFriendlyMessage = 'Google sign-in is not enabled. Please contact support.';
          break;
        default:
          userFriendlyMessage = `Sign-in failed: ${error.message}`;
      }
      
      const enhancedError = new Error(userFriendlyMessage);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      const currentUser = auth.currentUser;
      await signOut(auth);
      
      // Track sign-out
      if (currentUser) {
        logEvent(analytics, 'logout', {
          user_id: currentUser.uid
        });
      }
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Check if user is admin
  const checkAdminStatus = async (userId) => {
    try {
      const adminDoc = await getDoc(doc(db, 'admins', userId));
      return adminDoc.exists();
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User signed in' : 'User signed out');
      setUser(user);
      
      if (user) {
        const adminStatus = await checkAdminStatus(user.uid);
        setIsAdmin(adminStatus);
        console.log('Admin status:', adminStatus);
      } else {
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    isAdmin,
    signInWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 